import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { BRAND } from '../../theme/brandTokens';

const MembershipMonthlyPotPanel = ({
  detail,
  recognitionDate = null,
  estimated = false,
  currencyCode = DEFAULT_CURRENCY,
  showTitle = true,
}) => {
  const { t } = useTranslation();

  if (!detail) {
    return null;
  }

  const monthLabel = t(`employeePayments.payroll.monthNames.${detail.calendarMonth}`) || String(detail.calendarMonth);

  return (
    <Stack spacing={2.5}>
      {showTitle && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
            {t('employeePayments.payroll.membershipPot.title', { month: monthLabel, year: detail.calendarYear })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('employeePayments.payroll.membershipPot.subtitle')}
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {t('employeePayments.payroll.breakdown.recognitionDate')}:
          {' '}
          {recognitionDate ? formatUtcDate(`${recognitionDate}T00:00:00Z`) : '—'}
        </Typography>
        {estimated && (
          <Chip
            size="small"
            color="warning"
            label={t('employeePayments.payroll.estimated')}
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: `1px solid ${BRAND.line}`, backgroundColor: BRAND.background }}>
          <Typography variant="caption" color="text.secondary">{t('employeePayments.payroll.membershipPot.gross')}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
            {formatCurrency(detail.grossAmount, currencyCode)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: `1px solid ${BRAND.line}`, backgroundColor: BRAND.background }}>
          <Typography variant="caption" color="text.secondary">{t('employeePayments.payroll.membershipPot.storeMargin')}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
            {formatCurrency(detail.storeMarginAmount, currencyCode)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, border: `1px solid ${BRAND.line}`, backgroundColor: BRAND.background }}>
          <Typography variant="caption" color="text.secondary">{t('employeePayments.payroll.membershipPot.barberPool')}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
            {formatCurrency(detail.barberPoolAmount, currencyCode)}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t('employeePayments.payroll.membershipPot.totalPoints', { points: detail.totalPoints })}
      </Typography>

      <TableContainer sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: BRAND.background }}>
              <TableCell>{t('employeePayments.payroll.membershipPot.member')}</TableCell>
              <TableCell align="right">{t('employeePayments.payroll.membershipPot.points')}</TableCell>
              <TableCell align="right">{t('employeePayments.payroll.membershipPot.sharePercent')}</TableCell>
              <TableCell align="right">{t('employeePayments.payroll.membershipPot.shareAmount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(detail.memberShares || []).map((member) => (
              <TableRow key={member.teamMemberId} hover>
                <TableCell sx={{ fontWeight: 600 }}>{member.teamMemberName || '—'}</TableCell>
                <TableCell align="right">{member.points}</TableCell>
                <TableCell align="right">{member.sharePercent}%</TableCell>
                <TableCell align="right">{formatCurrency(member.shareAmount, currencyCode)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default MembershipMonthlyPotPanel;
